import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbDecoratedTextInput } from "abreact/input/AbDecoratedTextInput";
import { extractInputProps } from "abreact/input/AbInputProps";
import { AbSize, AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import * as React from "react";

/**
 * The properties for an AbPasswordInput.
 */

/**
 * The **AbPasswordInput** component allows the user to enter a single line of text.
 */
export function AbPasswordInput(props) {
  const contextSize = React.useContext(AbSizeContext);
  const [isCapsLockOn, setIsCapsLockOn] = React.useState(false);
  const [isPasswordShowing, setIsPasswordShowing] = React.useState(false);
  const [hasFocus, setHasFocus] = React.useState(false);

  // props for div: common props, size
  // props for text input: value, onChange, text input common props, abRef,
  //   placeholder, name, disabled, tabIndex, tooltip, autofocus
  const {
    value,
    onChange,
    size,
    textInputProps: textInputCommonProps,
    abRef,
    placeholder,
    isCurrentPassword
  } = ReactUtils.extractNonCommonProps(props);
  // name, disabled, tabIndex, tooltip, autofocus
  const inputProps = ReactUtils.extractNonCommonProps(extractInputProps(props));
  const commonProps = ReactUtils.extractCommonProps(props);
  const className = ReactUtils.componentClassNamesString("ab-password-input", props, ["abui", "ab-single-line", size ?? contextSize ?? AbSize.MEDIUM, isCapsLockOn && "ab-password-input-caps-lock-on"]);
  const divRef = React.useRef(null);
  const detectCapsLock = e => {
    setIsCapsLockOn(e.getModifierState("CapsLock"));
  };
  const decorators = [];
  if (hasFocus) {
    decorators.push({
      icon: isPasswordShowing ? "absdk:hide" : "absdk:show",
      tooltip: isPasswordShowing ? AbReactResources.passwordInput_hidePassword() : AbReactResources.passwordInput_showPassword(),
      className: "ab-password-toggle-icon",
      id: "ab-password-toggle-icon",
      onClick: () => {
        setIsPasswordShowing(!isPasswordShowing);
      },
      key: "ab-password-toggle-icon"
    });
    if (isCapsLockOn) {
      decorators.push({
        tooltip: AbReactResources.passwordInput_capsLockIndicator_tooltip(),
        image: "absdk:caps-lock-indicator",
        altText: AbReactResources.passwordInput_capsLockIndicator_tooltip(),
        className: "ab-password-input-caps-lock-icon-wrapper"
      });
    }
  }
  const inner = /*#__PURE__*/React.createElement(AbDecoratedTextInput, _extends({}, commonProps, {
    className: className,
    value: value,
    onChange: onChange,
    decorators: decorators,
    abRef: divRef,
    onFocus: () => {
      setHasFocus(true);
    },
    onBlur: e => {
      const relatedTarget = e.relatedTarget;
      if (relatedTarget === null || !divRef.current?.contains(relatedTarget)) {
        setHasFocus(false);
        setIsPasswordShowing(false);
      }
    },
    textInputProps: {
      ...inputProps,
      ...textInputCommonProps,
      placeholder,
      abRef,
      className: ReactUtils.classNamesString(["ab-base-password-input", textInputCommonProps?.className]),
      id: textInputCommonProps?.id ?? (props.id ? `${props.id}-input` : undefined),
      __htmlProps: {
        type: isPasswordShowing ? "text" : "password",
        autoComplete: isCurrentPassword ? "current-password" : "new-password",
        // It would be better to use onFocus, but focus events don't have modifier detection
        onKeyUp: detectCapsLock,
        onKeyDown: detectCapsLock,
        onClick: detectCapsLock
      }
    }
  }));
  return sizeProviderWrapper(inner, props, contextSize);
}
//# sourceMappingURL=AbPasswordInput.js.map
